import { renderPageShell } from '../lib/page-shell';
import { authorizedFetch, getToken } from '../lib/session';
import { escapeHtml } from '../lib/format';
import type { RouteHandler } from '../lib/router';

interface RecordCounts {
  played: number;
  wins: number;
  losses: number;
  ties: number;
  winRate: number;
}

interface OpponentStat {
  opponentUserId: string;
  label: string;
  played: number;
  wins: number;
  losses: number;
  ties: number;
}

interface PrecisionTrendPoint {
  weekStart: number;
  averageErrorPercent: number;
  roundsTimed: number;
}

interface TierCounts {
  green: number;
  yellow: number;
  orange: number;
  red: number;
}

interface PrecisionComparison {
  myTierCounts: TierCounts;
  myRoundsTimed: number;
  myTierPercentages: TierCounts;
  globalTierPercentages: TierCounts;
  morePreciseThanPercent: number | null;
}

interface PlayerStats {
  totalMatches: number;
  overall: RecordCounts;
  ranked: RecordCounts;
  casual: RecordCounts;
  rounds: { won: number; lost: number; winRate: number; avgWonPerMatch: number; avgLostPerMatch: number };
  streaks: { current: { type: string; length: number }; longestWin: number; longestLoss: number };
  rating: {
    current: number;
    peak: number;
    lowest: number;
    biggestGain: number;
    biggestLoss: number;
    averageDelta: number;
    history: { playedAt: number; rating: number }[];
  } | null;
  quality: { dominantWins: number; closeMatches: number; shutoutLosses: number; averageMargin: number };
  timing: {
    roundsTimed: number;
    falseStarts: number;
    falseStartRate: number;
    averageErrorMs: number;
    bestErrorMs: number;
    worstErrorMs: number;
    consistencyMs: number;
  } | null;
  opponents: { distinct: number; top: OpponentStat[]; nemesis: OpponentStat | null; favouriteVictim: OpponentStat | null };
  precisionTrend: PrecisionTrendPoint[];
  activity: {
    firstPlayedAt: number | null;
    lastPlayedAt: number | null;
    daysActive: number;
    busiestDay: { date: string; count: number } | null;
    last7Days: number;
    last30Days: number;
  };
}

const pct = (n: number) => `${Math.round(n * 100)}%`;
const signed = (n: number) => `${n >= 0 ? '+' : ''}${Math.round(n)}`;
const round1 = (n: number) => (Math.round(n * 10) / 10).toString();
const date = (ms: number | null) => (ms === null ? '-' : new Date(ms).toLocaleDateString());
const ms = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(2)} s` : `${Math.round(n)} ms`);

function tiles(items: { label: string; value: string; hint?: string }[]): string {
  return `<ul class="stat-tiles">${items
    .map(
      (i) =>
        `<li><strong>${i.value}</strong><span>${i.label}</span>${i.hint ? `<em>${i.hint}</em>` : ''}</li>`,
    )
    .join('')}</ul>`;
}

/** A tiny inline sparkline of the ranked rating over time. */
function sparkline(history: { rating: number }[]): string {
  if (history.length < 2) return '';
  const w = 260;
  const h = 56;
  const values = history.map((p) => p.rating);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = w / (values.length - 1);
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(h - ((v - min) / span) * (h - 6) - 3).toFixed(1)}`)
    .join(' ');
  return `
    <svg class="sparkline" viewBox="0 0 ${w} ${h}" role="img" aria-label="Rating over time" preserveAspectRatio="none">
      <polyline points="${points}" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />
    </svg>`;
}

/** A tiny inline sparkline of average precision error (% of target) per week — lower is better, plotted literally (not inverted) like every other value-over-time chart here. */
function precisionSparkline(trend: PrecisionTrendPoint[]): string {
  if (trend.length < 2) return '';
  const w = 260;
  const h = 56;
  const values = trend.map((p) => p.averageErrorPercent);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = w / (values.length - 1);
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(h - ((v - min) / span) * (h - 6) - 3).toFixed(1)}`)
    .join(' ');
  return `
    <svg class="sparkline" viewBox="0 0 ${w} ${h}" role="img" aria-label="Precision error over time" preserveAspectRatio="none">
      <polyline points="${points}" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />
    </svg>`;
}

const TIER_ORDER: (keyof TierCounts)[] = ['green', 'yellow', 'orange', 'red'];

/** A 100%-stacked horizontal bar, one segment per accuracy tier — same green/yellow/orange/red language as the per-attempt accuracy bar and session-overview colouring. */
function tierBar(percentages: TierCounts, label: string): string {
  const segments = TIER_ORDER.map(
    (tier) =>
      `<span class="precision-compare__segment precision-compare__segment--${tier}" style="flex-basis:${percentages[tier]}%" title="${tier}: ${round1(percentages[tier])}%"></span>`,
  ).join('');
  return `
    <div class="precision-compare__row">
      <span class="precision-compare__label">${label}</span>
      <div class="precision-compare__bar">${segments}</div>
    </div>`;
}

function opponentRows(list: OpponentStat[]): string {
  if (list.length === 0) return `<li class="attempt-list__empty">No identified opponents yet.</li>`;
  return list
    .map(
      (o) =>
        `<li><span>${escapeHtml(o.label)}</span><span>${o.played} played</span><strong>${o.wins}-${o.losses}${o.ties ? `-${o.ties}` : ''}</strong></li>`,
    )
    .join('');
}

function renderStatsBody(s: PlayerStats, comparison: PrecisionComparison | null): string {
  if (s.totalMatches === 0) {
    return `<p class="settings-hint">No matches played yet. Your stats will appear here once you start playing.</p>`;
  }

  const streakLabel =
    s.streaks.current.type === 'none'
      ? '-'
      : `${s.streaks.current.length} ${s.streaks.current.type}${s.streaks.current.length === 1 ? '' : 's'}`;

  const timingSection = s.timing
    ? `
      <section class="stat-section">
        <h2>Timing &amp; precision</h2>
        <p class="settings-hint">How close to the target you stop, across your online matches.</p>
        ${tiles([
          { label: 'AVG ERROR', value: ms(s.timing.averageErrorMs) },
          { label: 'BEST ROUND', value: ms(s.timing.bestErrorMs) },
          { label: 'WORST ROUND', value: ms(s.timing.worstErrorMs) },
          { label: 'CONSISTENCY', value: `±${ms(s.timing.consistencyMs)}`, hint: 'lower is steadier' },
          { label: 'ROUNDS TIMED', value: String(s.timing.roundsTimed) },
          { label: 'FALSE STARTS', value: String(s.timing.falseStarts), hint: pct(s.timing.falseStartRate) },
        ])}
        ${
          s.precisionTrend.length >= 2
            ? `<div class="rating-panel">${precisionSparkline(s.precisionTrend)}</div><p class="settings-hint">Weekly average error, as a % of that round's target; lower is better.</p>`
            : ''
        }
      </section>`
    : `<section class="stat-section"><h2>Timing &amp; precision</h2><p class="settings-hint">Play an online match to start recording your reaction timing.</p></section>`;

  const comparisonSection = comparison
    ? `
      <section class="stat-section">
        <h2>How you compare</h2>
        <p class="settings-hint">Every scored online round, yours and everyone else's, bucketed by how close it landed to its target.</p>
        ${
          comparison.morePreciseThanPercent !== null
            ? `<p class="precision-headline">More precise than <strong>${round1(comparison.morePreciseThanPercent)}%</strong> of every round ever played.</p>`
            : ''
        }
        <div class="precision-compare">
          ${tierBar(comparison.myTierPercentages, 'You')}
          ${tierBar(comparison.globalTierPercentages, 'Everyone')}
        </div>
        <ul class="precision-compare__legend">
          <li><span class="precision-swatch precision-swatch--green"></span>≤1%</li>
          <li><span class="precision-swatch precision-swatch--yellow"></span>≤3%</li>
          <li><span class="precision-swatch precision-swatch--orange"></span>≤5%</li>
          <li><span class="precision-swatch precision-swatch--red"></span>&gt;5%</li>
        </ul>
      </section>`
    : '';

  const ratingSection = s.rating
    ? `
      <section class="stat-section">
        <h2>Rating</h2>
        <div class="rating-panel">${sparkline(s.rating.history)}</div>
        ${tiles([
          { label: 'CURRENT', value: String(s.rating.current) },
          { label: 'PEAK', value: String(s.rating.peak) },
          { label: 'LOWEST', value: String(s.rating.lowest) },
          { label: 'BIGGEST WIN', value: signed(s.rating.biggestGain) },
          { label: 'BIGGEST LOSS', value: signed(s.rating.biggestLoss) },
          { label: 'AVG CHANGE', value: signed(s.rating.averageDelta) },
        ])}
      </section>`
    : `<section class="stat-section"><h2>Rating</h2><p class="settings-hint">Play a ranked match to start a rating.</p></section>`;

  return `
    <section class="stat-section">
      <h2>Overview</h2>
      ${tiles([
        { label: 'MATCHES', value: String(s.totalMatches) },
        { label: 'RECORD', value: `${s.overall.wins}-${s.overall.losses}-${s.overall.ties}` },
        { label: 'WIN RATE', value: pct(s.overall.winRate) },
        { label: 'RANKED', value: `${s.ranked.wins}-${s.ranked.losses}-${s.ranked.ties}`, hint: pct(s.ranked.winRate) },
        { label: 'CASUAL', value: `${s.casual.wins}-${s.casual.losses}-${s.casual.ties}`, hint: pct(s.casual.winRate) },
      ])}
    </section>

    ${timingSection}

    ${comparisonSection}

    ${ratingSection}

    <section class="stat-section">
      <h2>Rounds</h2>
      ${tiles([
        { label: 'ROUNDS WON', value: String(s.rounds.won) },
        { label: 'ROUNDS LOST', value: String(s.rounds.lost) },
        { label: 'ROUND WIN RATE', value: pct(s.rounds.winRate) },
        { label: 'AVG WON / MATCH', value: round1(s.rounds.avgWonPerMatch) },
      ])}
    </section>

    <section class="stat-section">
      <h2>Streaks &amp; form</h2>
      ${tiles([
        { label: 'CURRENT STREAK', value: streakLabel },
        { label: 'LONGEST WIN', value: String(s.streaks.longestWin) },
        { label: 'LONGEST LOSS', value: String(s.streaks.longestLoss) },
      ])}
    </section>

    <section class="stat-section">
      <h2>Match quality</h2>
      ${tiles([
        { label: 'DOMINANT WINS', value: String(s.quality.dominantWins), hint: '3-0' },
        { label: 'CLOSE MATCHES', value: String(s.quality.closeMatches), hint: 'by 1 round' },
        { label: 'SHUTOUT LOSSES', value: String(s.quality.shutoutLosses), hint: '0-3' },
        { label: 'AVG MARGIN', value: signed(s.quality.averageMargin) },
      ])}
    </section>

    <section class="stat-section">
      <h2>Opponents</h2>
      ${tiles([
        { label: 'DISTINCT', value: String(s.opponents.distinct) },
        { label: 'NEMESIS', value: s.opponents.nemesis ? s.opponents.nemesis.label : '-', hint: s.opponents.nemesis ? `${s.opponents.nemesis.losses} losses` : undefined },
        { label: 'FAVOURITE VICTIM', value: s.opponents.favouriteVictim ? s.opponents.favouriteVictim.label : '-', hint: s.opponents.favouriteVictim ? `${s.opponents.favouriteVictim.wins} wins` : undefined },
      ])}
      <h3 class="history-heading">Most played</h3>
      <ul class="attempt-list">${opponentRows(s.opponents.top)}</ul>
    </section>

    <section class="stat-section">
      <h2>Activity</h2>
      ${tiles([
        { label: 'FIRST PLAYED', value: date(s.activity.firstPlayedAt) },
        { label: 'LAST PLAYED', value: date(s.activity.lastPlayedAt) },
        { label: 'DAYS ACTIVE', value: String(s.activity.daysActive) },
        { label: 'BUSIEST DAY', value: s.activity.busiestDay ? String(s.activity.busiestDay.count) : '-', hint: s.activity.busiestDay?.date },
        { label: 'LAST 7 DAYS', value: String(s.activity.last7Days) },
        { label: 'LAST 30 DAYS', value: String(s.activity.last30Days) },
      ])}
    </section>`;
}

export const renderStats: RouteHandler = (container, navigate) => {
  const body = renderPageShell(container, navigate, 'account', {
    title: 'STATS',
    subtitle: 'Every number the record can give: records, rating, streaks, opponents and more.',
    body: `<div id="stats-mount"><p class="attempt-list__empty">Loading…</p></div>`,
  });

  const mount = body.querySelector<HTMLDivElement>('#stats-mount')!;

  if (!getToken()) {
    mount.innerHTML = `<p class="settings-hint">Sign in on the Account page to see your stats.</p>`;
    return;
  }

  authorizedFetch<{ stats: PlayerStats; precisionComparison: PrecisionComparison | null }>('/api/stats')
    .then(({ stats, precisionComparison }) => {
      mount.innerHTML = renderStatsBody(stats, precisionComparison);
    })
    .catch(() => {
      mount.innerHTML = `<p class="attempt-list__empty">Could not load stats.</p>`;
    });
};
